/**
 * Verification helpers (#137).
 *
 * Rejected accounts can now sign in — the backend caps that session to the
 * re-verification flow, which lives in the mobile app. The storefront has no
 * such flow, so it must refuse the session rather than drop the user into a
 * shell where every request 403s.
 */

/** The user object out of a stored login payload (`{ tokens, user }`) or a bare user. */
export function userFromSession(session) {
	if (!session || typeof session !== 'object') return null;
	return session.user && typeof session.user === 'object' ? session.user : session;
}

/** True for a signed-in, non-staff account the backend has not approved. */
export function isUnverifiedSession(session) {
	const user = userFromSession(session);
	if (!user) return false;
	if (user.is_staff === true) return false;
	return user.is_verified === false;
}

/** Staff rejection reason, when there is one. */
export function verificationNote(session) {
	const note = userFromSession(session)?.verification_note;
	return typeof note === 'string' ? note.trim() : '';
}

/** Whether the account was rejected (vs still pending review). */
export function isRejectedSession(session) {
	return userFromSession(session)?.verification_status === 'rejected';
}

/**
 * A session allowed to actually use the shop: signed in **and** approved.
 *
 * "Signed in" is not enough on its own — an unverified session holds a real
 * access token, but the backend caps it to the account's own record, so
 * anything that talks to a shop endpoint (cart sync, notifications, device
 * tokens) must gate on this or it just collects 403s (#145).
 */
export function isActiveSession(session) {
	return Boolean(session?.tokens?.access) && !isUnverifiedSession(session);
}
